import styled from "styled-components";

/* BV keeps each popover's content wrapper in the text flow directly after its
   link - an absolutely-positioned, hidden div. That out-of-flow box gives
   Chromium a line-break opportunity between the link and following
   punctuation, so BV can end a line on the link ("...company's stakeholders")
   and wrap the comma to the next line. Express portals popover content to
   document.body, losing that boundary: the link and the punctuation wrap as
   one unbreakable unit, one word earlier than BV (BVEW-2193). This empty
   out-of-flow marker, rendered right after each popover link, restores the
   boundary; it has no size and paints nothing. */
export const PopoverBreakOpportunity = styled.span`
  position: absolute;
`;

export const PopoverWrapper = styled.div<{
  isOpen?: boolean;
  hasYOverflow?: boolean;
  arrowXOffset?: number;
}>`
  position: fixed;
  display: flex;
  flex-direction: column;
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  border-radius: 4px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.1);
  font-family: inherit;
  font-size: 14px;
  line-height: 16px;
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  padding: 10px;
  text-decoration: none;
  user-modify: read-only;
  text-align: left;
  z-index: 10;
  background: var(--popover-surface, #fff);
  background-color: var(--popover-surface, #fff);
  color: var(--popover-text, #000);
  transition-property: opacity, visibility;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
  transition-delay: 0s, ${(props) => (props.isOpen ? "0s" : "0.2s")};
  cursor: auto;
  width: auto;
  max-width: min(320px, calc(100vw - 32px));
  max-height: calc(100vh - 80px);
  overflow: visible;
  box-sizing: border-box;

  @media (min-width: 768px) {
    width: 320px;
    max-width: 320px;
    max-height: 500px;
  }

  /* hasYOverflow = popover placed above the anchor (arrow on bottom, points down). Otherwise below (arrow on top, points up). */
  &::after {
    content: "";
    top: ${(props) => (props.hasYOverflow ? "unset" : "-10px")};
    bottom: ${(props) => (props.hasYOverflow ? "-10px" : "unset")};
    left: ${(props) => (props.arrowXOffset ? `${props.arrowXOffset}px` : "50%")};
    transform: ${(props) => (props.arrowXOffset ? "none" : "translateX(-50%)")};
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: ${(props) => (props.hasYOverflow ? "10px 10px 0 10px" : "0px 10px 10px 10px")};
    border-color: ${(props) =>
      props.hasYOverflow
        ? "var(--popover-surface, #fff) transparent transparent transparent"
        : "transparent transparent var(--popover-surface, #fff) transparent"};
  }

  * {
    visibility: ${(props) => (props.isOpen ? "visible" : "hidden !important")};
    transition-property: visibility;
    transition-duration: 0.2s;
    transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
    transition-delay: ${(props) => (props.isOpen ? "0s" : "0.2s")};
  }
`;

export const PopoverTitle = styled.h3`
  margin: 0px 20px 10px 0px;
  padding: 0px 10px 0px 0px;
  text-align: left;
  font-size: inherit;
  font-weight: inherit;
  color: var(--popover-text, #000);
  line-height: inherit;
  flex-shrink: 0;
`;

export const CloseButton = styled.button`
  position: absolute;
  color: var(--popover-text, #000);
  opacity: 0.4;
  cursor: pointer;
  float: right;
  top: 1%;
  right: 1%;
  width: 24px;
  height: 24px;
  padding: 0;
  background: 0;
  outline: 0;
  border: 0;
  margin: 2px 3px;

  &::before {
    content: "";
    position: absolute;
    width: 24px;
    height: 24px;
    left: 0;
    top: 1px;
    border-radius: 50%;
    opacity: 0;
    display: block;
    transition: opacity 0.25s ease-out;
    pointer-events: none;
    z-index: 0;
    background-color: #d2d2d2;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  &:focus {
    &::before {
      opacity: 1;
      outline: 1px auto #669df4;
    }
  }

  svg {
    position: relative;
    z-index: 1;
  }
`;

export const PopoverContent = styled.div`
  padding: 0;
  color: var(--popover-text, #000);
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;

  /* Ensure text wraps line by line */
  white-space: normal;
  word-break: break-word;

  /* Constrain ALL content to popover width */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }

  /* Ensure images and other content don't overflow horizontally */
  img {
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
    display: block;
    object-fit: contain;
  }

  /* Constrain stage content width */
  .spf-rce-stage {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  /* Constrain sections */
  .spf-rce-section {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .spf-rce-section-background-wrapper {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .spf-rce-section-content {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  /* Constrain rows and columns */
  .spf-rce-row {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .spf-rce-column {
    max-width: 100% !important;
    min-width: 0 !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  /* Ensure stage elements are constrained horizontally */
  .spf-rce-element {
    max-width: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
    overflow-x: hidden;
  }

  /* Constrain image containers and figures */
  figure {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0;
    box-sizing: border-box;
  }

  .image-container {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box;
  }

  /* Constrain layout components */
  .layout,
  .component-container,
  .component-label-section,
  .component-caption,
  .component-credit {
    max-width: 100% !important;
    box-sizing: border-box;
    overflow-x: hidden;
  }

  /* Ensure paragraphs and text elements wrap properly.
     !important beats any unscoped theme p rules that reach the popup. */
  p {
    margin: 0 0 8px 0 !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
